import StaticStore from '@/store/static'
import PageStore from '@/store/page'
import { iso3ToName } from '@/utils/countryMap'
import { timeMonth } from 'd3-time'
import { regionToName } from '@/utils/helpers'
import getCharts from '@/store/charts'
/**
 * @function location
 * Returns the current top level location (region or country)
 * @returns {String} - the current location
 */
const location = state => {
  return state.country || state.region
}
/**
 * @function fireKPI
 * Returns the Key indicators for the fire dimension
 * @returns {Object} [date, value]
 */
const fireKPI = (state, getters) => {
  const baseData = StaticStore.get(StaticStore.DIMENSION_FIRE, getters.location)
  const countries = baseData.columns.slice(1)
  const latestFires = baseData.slice(-1)
  let numFires = 0
  countries.forEach(d => {
    numFires += latestFires[0][d]
  })
  return {
    date: latestFires[0].date,
    value: numFires
  }
}
/**
 * @function forestLossKPI
 * Returns the Key indicators for the forestLoss dimension
 * @returns {Object} [year, value]
 */
const forestLossKPI = (state, getters) => {
  const baseData = StaticStore.get(StaticStore.DIMENSION_DEFORESTATION, getters.location)
  return {
    year: Math.min(...baseData.map(d => d.date)),
    value: Math.round(baseData.reduce(reduceObj, 0))
  }
}
/**
 * @function forestCoverKPI
 * Returns the Key indicators for the forestCover dimension
 * @returns {Object} [year, value]
 */
const forestCoverKPI = (state, getters) => {
  const baseData = StaticStore.get(StaticStore.DIMENSION_FORESTCOVER, getters.location)
  const latestYear = Math.max(...baseData.map(d => d.date))
  const forestCover = baseData.find(d => d.date === latestYear)
  const [, ...nums] = Object.values(forestCover)
  return {
    year: latestYear,
    value: Math.round(nums.reduce(reduceList, 0))
  }
}

/**
 * @function kpi
 * Returns the KPI for a specific (or the current) dimension
 * @param {String} dimension - [optional] the dimension to get the kpi for
 * @returns
 */
const kpi = (state, getters) => dimension => {
  const dim = dimension || state.dimension
  // get teh correct KPI
  switch (dim) {
    case 'forestCover':
      return getters.forestCoverKPI
    case 'forestLoss':
      return getters.forestLossKPI
    case 'fire':
      return getters.fireKPI
  }
}

/**
 * @function totals
 * Takes a data-array and sum up values for each date to new totals colum
 * @param  {String} dimension The dimension data to get
 * @return  {Array} The new data array with a columns property as per d3 convention
 */
const totals = (state, getters) => dimension => {
  const dim = dimension || state.dimension
  const data = StaticStore.get(dim, getters.location)
  return totalsFromData(data, dim)
}
/**
 * @function series
 * Creates an array of data series for use in multi line charts
 * @param  {String} dimension [optional] the dimension for the data, used ans name id for the chart
 * @param  {String} region [optional] the region to get data from
 * @param  {String} country [optional] the country to get data from
 * @return  {Array} Array of objects with keys [id, name, values] for each series where values is a array of {data and value}
 */
const series = (state, getters) => (dimension, region = null, country = null) => {
  const dim = dimension || state.dimension
  const location = region && country ? country || region : getters.location // use supplied region or country or pick the highest level of locations if none exists
  const data = StaticStore.get(dim, location)
  return seriesFromData(data, dim)
}

/**
 * @function seriesTotal
 * Creates an array of data series for use in multi line charts, data is aggregated at grend totals of all countries
 * @param  {String} dimension [optional] the dimension for the data, used ans name id for the chart
 * @return  {Array} Array of objects with keys [id, name, values] for each series where values is a array of {data and value}
 */
const seriesTotal = (state, getters) => (dimension, location) => {
  const dim = dimension || state.dimension
  location = location || getters.location
  const data = StaticStore.get(dim, location)
  const totals = totalsFromData(data, dim, state.country)
  return seriesFromData(totals, dim, regionToName(location, state.language))
}
/**
 * @function firesMonthlyTotals
 * Return the monthly totals for the fire data
 * @return  {Array} Array of objects with keys [id, name, values] for each series where values is a array of {data and value}
 */
const firesMonthlyTotals = (state, getters) => {
  const data = StaticStore.get('fire', getters.location)
  const countries = data.columns.slice(1) // remove date column
  const dates = data.map(d => d.date) // get all dates
  const months = timeMonth.range(dates[0], dates[dates.length - 1]) // get all unique months in date range
  const monthlyData = []

  // for each month, extract numbers for that month (filter) and reduce to monthly totals
  months.forEach(m => {
    const numbersByMonth = data.filter(d => +m === +timeMonth(d.date))
    var totals = numbersByMonth.reduce(
      (accumulator, value) => {
        countries.forEach(c => {
          if (c in accumulator) accumulator[c] += value[c]
          else accumulator[c] = value[c]
        })
        return accumulator
      },
      { date: m } // add date to monthly totals
    )
    monthlyData.push(totals)
  })
  return monthlyData
}
/**
 * @function tableData
 * Returns the data for each of the dimensions formatted for table view
 * @returns {Array}
 **/
const tableData = (state, getters) => {
  if (state.dimension === 'fire') {
    return transposeArray(getters.firesMonthlyTotals)
  }
  return transposeArray(StaticStore.get(state.dimension, getters.location))
}

/**
 * @function locale
 * Returns the locale as a function of the language selected
 * @returns the correct locale
 */
const locale = state => {
  return state.language === 'no' ? 'nb-NO' : 'en-GB'
}
/**
 * @function pageData
 * returns the page specific data (text, name, kpi++)
 * @param {String} dimension - [optionsl] the dimension to look for
 * @returns A page object as defined in '@/store/page
 */
const pageData = (state, getters) => dimension => {
  const dim = dimension || state.dimension
  const pageFunc = PageStore.get(state.language, dim)
  const kpi = getters.kpi(dim)
  return pageFunc(kpi.year, state.region) // fire has no kpi year, and the pageFunct does nor require it.. all well
  // console.log(text)
  // return text
}

const charts = state => () => {
  const country = state.country ? iso3ToName(state.country, state.language) : null
  return getCharts(state.region, state.dimension, state.language, country)
}

/**
 * @function localizedString
 * Returns a static string in the current language
 * @param {String} string - [optionsl] the id of the string (found in page.js#localizedStrings)
 * @param {String} language - [optionsl] the language to return the string for
 * @returns {String}
 */
const localizedString = state => (string, language) => {
  const lang = language || state.language
  return PageStore.localizedString(string, lang)
}

export default {
  location,
  fireKPI,
  forestLossKPI,
  forestCoverKPI,
  kpi,
  tableData,
  locale,
  pageData,
  charts,
  totals,
  firesMonthlyTotals,
  series,
  seriesTotal,
  localizedString
}

/** basic numeric reducer */
const reduceList = (accumulator, currentValue) => accumulator + currentValue
/** object reducer, ignores first prop as it is the date (hence [,..nums]) */
const reduceObj = (accumulator, currentValue) => {
  const [, ...nums] = Object.values(currentValue)
  return accumulator + nums.reduce(reduceList, 0)
}

/* flips an array */
const transposeArray = array => {
  const columns = Object.keys(array[0]).slice(1)
  const newArray = []
  columns.forEach(column => {
    const newRow = { country: column }
    array.forEach(row => {
      newRow[`y${row.date}`] = row[column] // prepend y for correct order of properties in object
    })
    newArray.push(newRow)
  })
  return newArray
}

/**
 * @function seriesFromData
 * Creates an array of data series for use in multi line charts
 * @param  {Array} data Data array
 * @param  {String} dimension - dimension, used as name id of coluuns and data
 * @param  {String} name - optional string to override lable/name for series
 * @return  {Array} Array of objects with keys [id, name, values] for each series where values is a array of {data and value}
 */
const seriesFromData = (data, dimension, name = undefined) => {
  const keys = data.columns.slice(1) // get countries
  const series = keys.map(id => {
    return {
      id: `${dimension}-${id}`,
      name: name || iso3ToName(id),
      values: data.map(d => {
        const value = +d[id]
        return {
          date: new Date(d.date.toString()),
          value
        }
      })
    }
  })
  return series
}
/**
 * @function totalsFromData
 * Takes a data-array and sum up values for each date to new totals colum
 * @param  {Array} data Data array
 * @param  {String} dimension Dimension, used to name the new totals column
 * @return  {Array} The new data array with a columns property as per d3 convention
 */
const totalsFromData = (data, dimension, country = null) => {
  const column = country || dimension
  const array = data.map(d => {
    const output = {}
    const sum = Object.values(d)
      .slice(1) // remove date column
      .reduce((t, n) => t + n)
    output.date = d.date
    output[column] = sum
    return output
  })
  array['columns'] = ['date', column]
  return array
}
